import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./ui/accordion";
import { HelpCircle } from "lucide-react";

const ichraFaqs = [
  {
    q: "What size company can offer ICHRA?",
    a: "Any size. ICHRA has no minimum or maximum employee count. It works for a 3-person team or a 300-person team, which is one of the biggest advantages over traditional group health plans that often have minimum participation rules.",
  },
  {
    q: "Can my employees still get marketplace subsidies?",
    a: "It depends on whether the ICHRA allowance is considered affordable for that employee. If the ICHRA offer is affordable based on IRS rules, employees cannot also claim premium tax credits. If it is not affordable, they can choose to take the ICHRA reimbursement or decline it and use marketplace subsidies instead. We help you model this for your team before setup.",
  },
  {
    q: "Can I offer ICHRA and a traditional group plan at the same time?",
    a: "Yes, but not to the same group of employees. You can offer a group plan to one class of employees (for example, full-time salaried) and an ICHRA to another class (for example, part-time or remote workers). This is a powerful strategy for businesses with mixed workforces.",
  },
  {
    q: "Can I set different allowances for different employees?",
    a: "Yes. ICHRA allows you to customize allowances across 11 IRS-approved employee classes including full-time, part-time, seasonal, salaried, hourly, by location, by age, and more. This gives you real flexibility to control costs and tailor benefits.",
  },
  {
    q: "What happens to HSAs if I switch to ICHRA?",
    a: "Employees can still contribute to an HSA, but only if the individual health plan they enroll in is HSA-eligible (a qualified high-deductible plan), and only if your ICHRA is structured as 'limited purpose' for HSA compatibility. We help you set this up correctly so your team does not lose HSA access.",
  },
  {
    q: "Is ICHRA actually IRS approved?",
    a: "Yes. ICHRA was established by federal regulations from the IRS, Department of Labor, and Department of Health and Human Services effective January 2020. It is fully legal, codified in federal law, and used by thousands of businesses nationwide.",
  },
  {
    q: "What if an employee does not use their full monthly allowance?",
    a: "You only reimburse what employees actually spend on eligible premiums and expenses. Unused allowance does not roll over to you as a cost, and you do not pay it out as cash. This is part of what makes ICHRA so cost-predictable for employers.",
  },
  {
    q: "How long does ICHRA setup take?",
    a: "For most businesses, we can get an ICHRA designed, documented, and ready for employee enrollment within a few weeks. We handle the plan document, the employee notice, and the reimbursement infrastructure so you do not have to figure it out alone.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": ichraFaqs.map((f) => ({
    "@type": "Question",
    "name": f.q,
    "acceptedAnswer": { "@type": "Answer", "text": f.a },
  })),
};

export const ICHRAFAQSection = () => (
  <section data-testid="ichra-faq-section" className="py-20 bg-white">
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <div className="inline-flex items-center space-x-2 bg-[#EAF9F9] text-[#2EC4C4] font-semibold text-xs uppercase tracking-wider px-4 py-2 rounded-full mb-5">
          <HelpCircle size={14} />
          <span>ICHRA Questions</span>
        </div>
        <h2 className="font-bold text-3xl lg:text-4xl text-[#1A2744] mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
          Common ICHRA Questions From Employers
        </h2>
        <p className="text-[#475569] text-lg max-w-2xl mx-auto">
          Straight answers to the questions business owners ask us most often about ICHRA.
        </p>
      </div>

      <Accordion type="single" collapsible className="bg-[#F8FAFC] rounded-2xl border border-gray-100 shadow-sm px-6 sm:px-8">
        {ichraFaqs.map((faq, i) => (
          <AccordionItem
            key={faq.q}
            value={`ichra-faq-${i}`}
            data-testid={`ichra-faq-item-${i}`}
            className="border-b border-gray-200 last:border-0"
          >
            <AccordionTrigger className="text-left font-semibold text-base sm:text-lg text-[#1A2744] py-5 hover:no-underline" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {faq.q}
            </AccordionTrigger>
            <AccordionContent className="text-[#475569] text-sm sm:text-base leading-relaxed pb-5">
              {faq.a}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  </section>
);
